import { useCallback, useMemo } from 'react'

import { useAtomValue } from 'jotai'

import { Option } from '@sequencehq/core-components'

import { presentationAtom } from 'Automations/automationEditor/atoms'
import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import {
  ConditionalOperator,
  ConditionGridStyle,
  ConditionPropertyEditorComponent,
  ConditionValueInputComponent
} from 'Automations/nodeDefinitions/conditions'
import { ValidationResult } from 'Automations/nodeDefinitions/validation'
import { getValidationErrorsByPath } from 'Automations/nodeDefinitions/validation/helpers/getValidationErrorsByPath/getValidationErrorsByPath'
import { useGroupEditing } from '../useGroupEditing'
import { useUpstreamConditionalProperties } from '../useUpstreamConditionalProperties'
import type { Condition } from '../../switch.types'
import { getAvailableOperators, getAvailableProperties } from '../../helpers'

type UseConditionEditing = (params: {
  nodeId: string
  conditionId: string
}) => {
  condition: Condition | undefined
  updateCondition: (params: Partial<Omit<Condition, 'id'>>) => void
  availableProperties: Option[]
  availableOperators: Option[]
  valueInputComponent: ConditionValueInputComponent | undefined
  /**
   * Property-level editor component. When defined, the row delegates the
   * entire post-property UI (operator dropdown + value editing) to this
   * component instead of the framework's default layout.
   */
  propertyEditorComponent: ConditionPropertyEditorComponent | undefined
  propertyEditorLayout: 'inline' | undefined
  /**
   * The full set of operator definitions for the picked property, so
   * property-level editors can render their own operator dropdown.
   */
  propertyOperators: Record<string, ConditionalOperator> | undefined
  requiresValue: boolean
  /**
   * Resolved grid style for the operator+value row, derived from the operator
   * config's `gridStyle(condition)` callback when present.
   */
  gridStyle: ConditionGridStyle | undefined
  /**
   * Full `Condition` records for the other rows in the same group. Surfaced
   * to property-level editors so they can apply cross-operator rules.
   */
  siblingConditions: Array<Condition>
  /**
   * Every node in the workflow. Surfaced to property-level editors whose
   * subject is another node.
   */
  nodes: AutomationsPresentationV1Node[]
  /**
   * Returns whether the operator dropdown should auto-open after the user
   * picks the given property key. Mirrors the property's
   * `focusOperatorOnSelect` config (defaults to `true`). Takes the new
   * property key so the caller can check this inside the property
   * dropdown's `onChange` before the condition state updates.
   */
  focusOperatorOnSelect: (propertyKey: string) => boolean
  canRemove: boolean
  onRemove: () => void
  validationErrors: Record<string, ValidationResult[]>
}

export const useConditionEditing: UseConditionEditing = ({
  nodeId,
  conditionId
}) => {
  const {
    group,
    updateGroup,
    conditions,
    removeCondition,
    validationErrors: nodeValidationErrors
  } = useGroupEditing({ nodeId })

  const presentation = useAtomValue(presentationAtom)

  const condition = useMemo(() => {
    return conditions.find(({ id }) => conditionId === id)
  }, [conditionId, conditions])

  /**
   * Properties
   */
  const allProperties = useUpstreamConditionalProperties(nodeId, presentation)
  // A property whose subject is another node decides its own availability from
  // these — no review step in the workflow, no previous approval to compare.
  const nodes = presentation?.nodes ?? []

  const updateCondition = useCallback(
    (params: Partial<Omit<Condition, 'id'>>) => {
      // Picking a new property auto-defaults to the first available operator
      // for that property (and resets value/unit) so the user can move straight
      // to the operator/value flow without an extra click.
      const paramsWithDefaultOperator = (() => {
        if (
          !params.property ||
          params.operator !== undefined ||
          !allProperties
        ) {
          return params
        }
        const newPropertyDef = allProperties[params.property]
        if (!newPropertyDef) return params

        const siblingConditions = conditions.filter(
          sibling => sibling.id !== conditionId
        )
        const defaultOperator = newPropertyDef.getDefaultOperator
          ? newPropertyDef.getDefaultOperator({ siblingConditions })
          : getAvailableOperators({
              conditionId,
              propertyKey: params.property,
              conditions,
              property: newPropertyDef
            }).at(0)

        return {
          ...params,
          operator: defaultOperator ?? '',
          value: ''
        }
      })()

      // The property used for operator lookups is the one being applied
      // (params take precedence over the current condition state).
      const effectiveProperty =
        paramsWithDefaultOperator.property ?? condition?.property

      const resolvedParams = (() => {
        if (
          !paramsWithDefaultOperator.operator ||
          !effectiveProperty ||
          !allProperties
        ) {
          return paramsWithDefaultOperator
        }

        const operatorDef =
          allProperties[effectiveProperty]?.operators[
            paramsWithDefaultOperator.operator
          ]

        if (operatorDef?.requiresValue === false) {
          return { ...paramsWithDefaultOperator, value: true }
        }

        const currentOperatorDef = condition?.operator
          ? allProperties[effectiveProperty]?.operators[condition.operator]
          : undefined

        if (currentOperatorDef?.requiresValue === false) {
          return { ...paramsWithDefaultOperator, value: '' }
        }

        return paramsWithDefaultOperator
      })()

      updateGroup({
        conditions: conditions.map(c => {
          if (conditionId !== c.id) return c
          return { ...c, ...resolvedParams }
        })
      })
    },
    [
      conditionId,
      conditions,
      updateGroup,
      allProperties,
      condition?.property,
      condition?.operator
    ]
  )

  const availableProperties = useMemo(() => {
    if (!allProperties) return []

    const propertyKeys = getAvailableProperties({
      conditionId,
      conditions,
      allProperties,
      nodes
    })

    return propertyKeys.map(property => ({
      label: allProperties[property].label,
      value: property
    }))
  }, [allProperties, conditionId, conditions, nodes])

  /**
   * Operators
   */
  const availableOperators = useMemo(() => {
    if (!condition?.property || !allProperties) return []

    const property = allProperties[condition.property]

    const operatorKeys = getAvailableOperators({
      conditionId,
      propertyKey: condition.property,
      conditions,
      property
    })

    return operatorKeys.map(operator => ({
      label: property.operators[operator].label,
      value: operator
    }))
  }, [condition?.property, allProperties, conditionId, conditions])

  /**
   * Input
   */
  const operatorDef = useMemo(() => {
    if (!allProperties || !condition?.property || !condition?.operator) {
      return undefined
    }

    return allProperties[condition.property].operators[condition.operator]
  }, [allProperties, condition?.property, condition?.operator])

  const valueInputComponent = operatorDef?.valueInputComponent
  const requiresValue = Boolean(operatorDef?.requiresValue !== false)
  const gridStyle =
    operatorDef?.gridStyle && condition
      ? operatorDef.gridStyle(condition)
      : undefined

  const propertyDef = useMemo(() => {
    if (!allProperties || !condition?.property) return undefined
    return allProperties[condition.property]
  }, [allProperties, condition?.property])

  const propertyEditorComponent = propertyDef?.propertyEditorComponent
  const propertyEditorLayout = propertyDef?.propertyEditorLayout
  const propertyOperators = propertyDef?.operators

  const siblingConditions = useMemo(() => {
    if (!condition?.property) return []
    return conditions.filter(
      c => c.id !== conditionId && c.property === condition.property
    )
  }, [conditionId, conditions, condition?.property])

  const focusOperatorOnSelect = useCallback(
    (propertyKey: string) =>
      allProperties?.[propertyKey]?.focusOperatorOnSelect !== false,
    [allProperties]
  )

  /**
   * Removing conditions
   */
  const canRemove = useMemo(() => conditions.length > 1, [conditions.length])

  const onRemove = useCallback(
    () => removeCondition(conditionId),
    [conditionId, removeCondition]
  )

  const validationErrors = useMemo(() => {
    if (!group) return {}
    return getValidationErrorsByPath(
      nodeValidationErrors,
      `groups.${group.id}.conditions.${conditionId}`
    )
  }, [nodeValidationErrors, group, conditionId])

  return {
    condition,
    updateCondition,
    availableProperties,
    availableOperators,
    valueInputComponent,
    propertyEditorComponent,
    propertyEditorLayout,
    propertyOperators,
    requiresValue,
    gridStyle,
    siblingConditions,
    nodes,
    focusOperatorOnSelect,
    canRemove,
    onRemove,
    validationErrors
  }
}
